/**
 * UserSearch Component
 * 
 * Allows users to search for other users by their verified tag.
 * Displays search results and enables starting new chats.
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../contexts/AuthContext';
import { searchUsersByTag, createOrGetChat } from '../services/db';

const UserSearch = ({ onStartChat }) => {
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');

  /**
   * Handle search input
   */
  const handleSearch = async (query) => {
    setSearchQuery(query);

    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    // Basic validation - should be in format @Name#YT
    if (!query.includes('#YT') || !query.startsWith('@')) {
      setError('Please enter a valid tag in format @ChannelName#YT');
      setSearchResults([]);
      return;
    }

    setError('');
    setIsSearching(true);

    try {
      const results = await searchUsersByTag(query.trim());
      // Filter out current user from results
      const filteredResults = results.filter(user => user.id !== currentUser.uid);
      setSearchResults(filteredResults);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search users. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  /**
   * Handle starting a chat with a user
   */
  const handleStartChat = async (user) => {
    try {
      const chatId = await createOrGetChat(currentUser.uid, user.id);
      onStartChat(chatId, user);
      setSearchQuery('');
      setSearchResults([]);
    } catch (error) {
      console.error('Error starting chat:', error);
      alert('Failed to start chat. Please try again.');
    }
  };

  return (
    <div className="p-4 border-b border-gray-200 bg-white">
      <h3 className="text-lg font-semibold text-gray-800 mb-3">Find Users</h3>

      {/* Search input */}
      <div className="relative mb-4">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search by verified tag (e.g., @ChannelName#YT)"
          className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {isSearching ? (
            <svg className="animate-spin h-5 w-5 text-gray-400" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Search results */}
      {searchResults.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Search Results</h4>
          {searchResults.map((user) => (
            <div
              key={user.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <img
                  src={user.avatarURL || '/default-avatar.png'}
                  alt={user.displayName}
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <p className="font-medium text-gray-900">{user.displayName}</p>
                  <p className="text-sm text-gray-600">{user.verifiedTag}</p>
                </div>
              </div>
              <button
                onClick={() => handleStartChat(user)}
                className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Message
              </button>
            </div>
          ))}
        </div>
      )}

      {/* No results */}
      {searchQuery && !isSearching && searchResults.length === 0 && !error && (
        <div className="text-center py-8">
          <div className="text-4xl mb-2">🔍</div>
          <p className="text-gray-600">No users found with that tag</p>
          <p className="text-sm text-gray-500 mt-1">
            Make sure the tag is in the correct format: @ChannelName#YT
          </p>
        </div>
      )}

      {/* Search tips */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <h5 className="text-sm font-medium text-blue-800 mb-1">Search Tips</h5>
        <ul className="text-xs text-blue-700 space-y-1">
          <li>• Verified tags are in format @ChannelName#YT</li>
          <li>• Tags are case-sensitive</li>
          <li>• You can only message users who allow it in their privacy settings</li>
        </ul>
      </div>
    </div>
  );
};

UserSearch.propTypes = {
  onStartChat: PropTypes.func.isRequired,
};

export default UserSearch;
